import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Box from '@mui/material/Box';

import { StackedBarChart } from '../../shared/charts';

import '../dashboard.scss';

const CaseProceduresChart = ({ cases = [] }) => {
  const getTopProceduresOverall = (cases, topN = 5) => {
    const map = {};

    cases.forEach((c) => {
      const name = c?.procedure || 'Unknown';
      map[name] = (map[name] || 0) + 1;
    });

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([name]) => name);
  };

  const buildMonthlyData = (cases, procedureNames, showTopN = 5) => {
    const monthsMap = {};

    for (let i = 11; i >= 0; i--) {
      const date = moment().subtract(i, 'months');
      const key = date.format('YYYY-MM');

      monthsMap[key] = {
        month: date.format('MMM'),
      };

      procedureNames.forEach((name) => {
        monthsMap[key][name] = 0;
      });
    }

    cases.forEach((c) => {
      const monthKey = moment(c.createdAt).format('YYYY-MM');
      const name = c?.procedure || 'Unknown';

      if (!monthsMap[monthKey]) return;
      if (!procedureNames.includes(name)) return;

      monthsMap[monthKey][name]++;
    });

    return Object.values(monthsMap).map((monthData) => {
      const entries = Object.entries(monthData)
        .filter(([key]) => key !== 'month')
        .sort((a, b) => b[1] - a[1]);

      const top = entries.slice(0, showTopN);
      const rest = entries.slice(showTopN);

      const formatted = { month: monthData.month };

      top.forEach(([name, count]) => {
        formatted[name] = count;
      });

      const otherTotal = rest.reduce((sum, [, count]) => sum + count, 0);

      if (otherTotal > 0) {
        formatted.Other = otherTotal;
      }

      return formatted;
    });
  };

  const procedures = useMemo(() => {
    return getTopProceduresOverall(cases, 20);
  }, [cases]);

  const data = useMemo(() => {
    return buildMonthlyData(cases, procedures);
  }, [cases, procedures]);

  return (
    <Box className="dashboard-graph-container">
      <div className="table-title-container">
        <div className="flex-1">
          <div className="d-flex">
            <Box>
              <Box className="table-title">
                Cases
                <Box className="badge">by procedure</Box>
              </Box>

              <Box className='table-subtitle'>
                Last 12 months
              </Box>
            </Box>
          </div>
        </div>
      </div>

      <Box sx={{ padding: '0 24px 24px 0' }}>
        <StackedBarChart data={data} bars={procedures} stackId="procedure" />
      </Box>
    </Box>
  );
};

CaseProceduresChart.propTypes = {
  cases: PropTypes.array,
};

export default memo(CaseProceduresChart);
